import { type SearchSchemaInput, createFileRoute } from "@tanstack/react-router";
import type * as React from "react";

import {
  ConsoleDocumentsPage,
  consoleDocumentJobsQueryOptions,
  consoleDocumentsQueryOptions,
} from "@/features/console";

/**
 * Deep-linkable URL state: ``?page=`` for pagination, ``?doc=<id>`` for
 * the open document-detail modal.
 */
interface DocumentsSearch {
  page: number;
  doc?: string;
  q?: string;
  status?: "completed" | "running" | "queued" | "failed";
  sort?: "name" | "size" | "created_at";
  dir?: "asc" | "desc";
}

export const Route = createFileRoute("/_authed/_modal/console/documents")({
  // ``SearchSchemaInput``: links may omit the search entirely (defaults to
  // page 1) while the validated output always carries ``page``.
  validateSearch: (raw: { page?: number; doc?: string; q?: string; status?: string; sort?: string; dir?: string } & SearchSchemaInput): DocumentsSearch => {
    const rawPage: unknown = raw.page;
    const parsed = typeof rawPage === "number" ? rawPage : Number(rawPage);
    const page = Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
    const doc = typeof raw.doc === "string" && raw.doc.length > 0 ? raw.doc : undefined;
    const q = typeof raw.q === "string" ? raw.q.trim() : "";
    const status = ["completed", "running", "queued", "failed"].includes(raw.status ?? "") ? raw.status as DocumentsSearch["status"] : undefined;
    const sort = ["name", "size", "created_at"].includes(raw.sort ?? "") ? raw.sort as DocumentsSearch["sort"] : undefined;
    const dir = raw.dir === "asc" || raw.dir === "desc" ? raw.dir : undefined;
    return { page, ...(doc ? { doc } : {}), ...(q ? { q } : {}), ...(status ? { status } : {}), ...(sort ? { sort } : {}), ...(sort && dir ? { dir } : {}) };
  },
  loaderDeps: ({ search }) => ({ ...search }),
  loader: ({ context: { queryClient }, deps }) => {
    void queryClient.ensureQueryData(consoleDocumentsQueryOptions(deps.page, { search: deps.q, status: deps.status, sort_by: deps.sort, sort_order: deps.dir }));
    if (deps.doc !== undefined) {
      void queryClient.ensureQueryData(consoleDocumentJobsQueryOptions(deps.doc));
    }
  },
  component: DocumentsRoute,
});

function DocumentsRoute(): React.ReactElement {
  const { page, doc, q, status, sort, dir } = Route.useSearch();
  return <ConsoleDocumentsPage page={page} docId={doc ?? null} filters={{ search: q, status, sort_by: sort, sort_order: dir }} />;
}
